// src/utils/filesComposees.ts — SCE Issues
// Les files qu'une personne compose pour son accueil (lot 5 du chantier du 03/11,
// docs/chantier-accueil.md) — deux origines, décision de Guillaume :
//
//  - EN BULLES, à l'accueil : métier, statut, criticité, « ne garder que » mes
//    déclarations / ce que je suis / en fabrication, sans activité depuis N jours,
//    et un ordre. Rangée avec les filtres d'une vue du tableau de /issues (même
//    forme, mêmes règles : `filtrer`), plus deux critères propres à l'accueil.
//  - ÉPINGLÉE depuis une vue personnelle de « Tous les problèmes » : la file reste
//    LIÉE à la vue (filtres et tri relus à chaque fois) ; vue supprimée = file disparue.
//
// Pur : aucun réseau, aucun React.

import type { Issue } from '@/types/issue.types'
import {
  appliquerVue,
  filtrer,
  reglagesParDefaut,
  trier,
  type ContexteTableau,
  type CritereTri,
  type FiltresColonnes,
  type VuePerso,
} from './tableauProblemes'

/** Six compteurs au plus : au-delà, la rangée ne se lit plus. */
export const MAX_FILES = 6
export const LONGUEUR_MAX_NOM_FILE = 32

export type OrdreFile = 'ancien' | 'recent' | 'criticite' | 'silence'

export const ORDRES_FILE: readonly { ordre: OrdreFile; libelle: string }[] = [
  { ordre: 'ancien', libelle: 'Plus ancien d’abord' },
  { ordre: 'recent', libelle: 'Plus récent d’abord' },
  { ordre: 'criticite', libelle: 'Plus critique d’abord' },
  { ordre: 'silence', libelle: 'Plus long silence d’abord' },
]

export interface FileComposee {
  /** `c-…` (composée en bulles) ou `v-<id de la vue>` (épinglée depuis /issues). */
  id: string
  nom: string
  filtres: FiltresColonnes
  mesDeclarations: boolean
  suivis: boolean
  /** Équipements de la liste « en fabrication » (dossier M:) seulement. */
  enFabrication: boolean
  /** Rien depuis N jours (dernier événement, ou la déclaration). */
  sansActiviteDepuis: number | null
  ordre: OrdreFile
  /** Liée à cette vue personnelle de /issues ; null : composée en bulles. */
  vue: string | null
}

/** Ce dont le calcul a besoin, en plus du contexte du tableau. */
export interface ContexteFile extends ContexteTableau {
  enFabrication: (issue: Issue) => boolean
  /** Dernier événement de chaque problème (ms), sinon sa déclaration. */
  activites: ReadonlyMap<string, number>
}

const JOUR_MS = 86_400_000
const estObjet = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

/** Une file composée neuve : les problèmes actifs du périmètre, du plus ancien au plus récent. */
export function nouvelleFile(metiers: readonly string[], id: string = `c-${Date.now().toString(36)}`): FileComposee {
  return {
    id,
    nom: '',
    filtres: { statut: ['Ouvert', 'En cours'], ...(metiers.length ? { metier: [...metiers] } : {}) },
    mesDeclarations: false,
    suivis: false,
    enFabrication: false,
    sansActiviteDepuis: null,
    ordre: 'ancien',
    vue: null,
  }
}

export const idDeVue = (vueId: string): string => `v-${vueId}`

/** La file liée à une vue personnelle (« Épingler à l'accueil »). */
export function fileDeVue(vue: VuePerso): FileComposee {
  return { ...nouvelleFile([], idDeVue(vue.id)), filtres: {}, nom: vue.nom, vue: vue.id }
}

// ─── Lecture tolérante ───

function lireFiltres(v: unknown): FiltresColonnes {
  if (!estObjet(v)) return {}
  const res: Record<string, string[]> = {}
  for (const [col, valeurs] of Object.entries(v)) {
    if (!Array.isArray(valeurs)) continue
    const textes = valeurs.filter((x): x is string => typeof x === 'string')
    if (textes.length) res[col] = textes
  }
  return res as FiltresColonnes
}

function lireFile(v: unknown): FileComposee | null {
  if (!estObjet(v) || typeof v.id !== 'string' || !/^[cv]-[\w-]{1,60}$/.test(v.id)) return null
  const nom = typeof v.nom === 'string' ? v.nom.trim().slice(0, LONGUEUR_MAX_NOM_FILE) : ''
  const vue = typeof v.vue === 'string' && v.vue ? v.vue : null
  if (!nom && !vue) return null
  const delai = v.sansActiviteDepuis
  return {
    id: v.id,
    nom,
    filtres: lireFiltres(v.filtres),
    mesDeclarations: v.mesDeclarations === true,
    suivis: v.suivis === true,
    enFabrication: v.enFabrication === true,
    sansActiviteDepuis: typeof delai === 'number' && Number.isInteger(delai) && delai > 0 && delai <= 365 ? delai : null,
    ordre: ORDRES_FILE.some((o) => o.ordre === v.ordre) ? (v.ordre as OrdreFile) : 'ancien',
    vue,
  }
}

export function lireFilesComposees(v: unknown): FileComposee[] {
  if (!Array.isArray(v)) return []
  const vus = new Set<string>()
  const res: FileComposee[] = []
  for (const brut of v) {
    const f = lireFile(brut)
    if (!f || vus.has(f.id)) continue
    vus.add(f.id)
    res.push(f)
  }
  return res
}

// ─── Calcul ───

/**
 * La file prête à calculer : une file liée prend le nom, les filtres et le tri
 * de SA vue telle qu'elle est aujourd'hui ; vue supprimée = null (la file disparaît).
 */
export function resoudreFile(f: FileComposee, vues: readonly VuePerso[]): (FileComposee & { tri: CritereTri[] | null }) | null {
  if (!f.vue) return { ...f, tri: null }
  const vue = vues.find((v) => v.id === f.vue)
  if (!vue) return null
  return { ...f, nom: vue.nom, filtres: vue.filtres, mesDeclarations: vue.mesDeclarations, suivis: vue.suivis, tri: vue.tri }
}

const TRI_PAR_ORDRE: Record<Exclude<OrdreFile, 'silence'>, CritereTri[]> = {
  ancien: [{ col: 'date', sens: 'asc' }],
  recent: [{ col: 'date', sens: 'desc' }],
  criticite: [{ col: 'criticite', sens: 'asc' }, { col: 'date', sens: 'asc' }],
}

const derniere = (i: Issue, ctx: Pick<ContexteFile, 'activites'>): number => ctx.activites.get(i.id) ?? Date.parse(i.dateDeclaration)

/** Jours depuis le dernier événement (ou la déclaration). */
export function joursDeSilence(i: Issue, ctx: Pick<ContexteFile, 'activites' | 'maintenant'>): number {
  return Math.max(0, Math.floor((ctx.maintenant - derniere(i, ctx)) / JOUR_MS))
}

/** Les problèmes de la file, dans son ordre. */
export function problemesDeLaFile(f: FileComposee & { tri: CritereTri[] | null }, issues: readonly Issue[], ctx: ContexteFile): Issue[] {
  const reglages = appliquerVue(reglagesParDefaut(), {
    id: f.id,
    libelle: f.nom,
    filtres: f.filtres,
    mesDeclarations: f.mesDeclarations,
    suivis: f.suivis,
  })
  let res = filtrer(issues, reglages, '', ctx)
  if (f.enFabrication) res = res.filter(ctx.enFabrication)
  const delai = f.sansActiviteDepuis
  if (delai) res = res.filter((i) => joursDeSilence(i, ctx) >= delai)
  if (f.tri) return trier(res, f.tri.length ? f.tri : TRI_PAR_ORDRE.recent, ctx)
  if (f.ordre === 'silence') return [...res].sort((a, b) => derniere(a, ctx) - derniere(b, ctx))
  return trier(res, TRI_PAR_ORDRE[f.ordre], ctx)
}

/** La plus longue attente (jours depuis le dernier événement) : seuil d'alerte. */
export function attenteDeLaFile(issues: readonly Issue[], ctx: ContexteFile): number {
  return Math.max(0, ...issues.map((i) => joursDeSilence(i, ctx)))
}

/** Pourquoi ce nom ne convient pas, ou null. */
export function erreurNomFile(nom: string, autres: readonly string[]): string | null {
  const n = nom.trim()
  if (!n) return 'Donnez un nom à la file.'
  if (n.length > LONGUEUR_MAX_NOM_FILE) return `${LONGUEUR_MAX_NOM_FILE} caractères au plus.`
  if (autres.some((a) => a.trim().toLowerCase() === n.toLowerCase())) return 'Une file porte déjà ce nom.'
  return null
}
